import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const footerLinks = {
  platform: [
    { name: 'Bible Reader', href: '/bible-reader' },
    { name: 'Audio Library', href: '/audio-book-library' },
    { name: 'Live Streams', href: '/live-streams-hub' },
    { name: 'Digital Library', href: '/digital-books-library' }
  ],
  community: [
    { name: 'Discussion Forums', href: '/community-hub' },
    { name: 'Prayer Requests', href: '/community-hub' },
    { name: 'Study Groups', href: '/community-hub' },
    { name: 'Mentorship', href: '/community-hub' }
  ],
  resources: [
    { name: 'Daily Devotionals', href: '/personal-dashboard' },
    { name: 'Study Guides', href: '/digital-books-library' },
    { name: 'Theological Articles', href: '/digital-books-library' },
    { name: 'Women in Scripture', href: '/bible-reader' }
  ],
  support: [
    { name: 'Help Center', href: '/community-hub' },
    { name: 'Contact Us', href: '/community-hub' },
    { name: 'Privacy Policy', href: '/privacy' },
    { name: 'Terms of Service', href: '/terms' }
  ]
};

const socialLinks = [
  { name: 'Facebook', icon: 'ShareIcon', href: 'https://facebook.com/pneumafreedomexperience' },
  { name: 'Twitter', icon: 'ChatBubbleLeftRightIcon', href: 'https://twitter.com/pneumafreedom' },
  { name: 'Instagram', icon: 'PhotoIcon', href: 'https://instagram.com/pneumafreedomexperience' },
  { name: 'YouTube', icon: 'VideoCameraIcon', href: 'https://youtube.com/pneumafreedomexperience' }
];

export default function Footer() {
  const currentYear = new Date()?.getFullYear();

  return (
    <footer className="bg-foreground text-background">
      <div className="max-w-7xl mx-auto px-4 py-16">
        {/* Main Footer Content */}
        <div className="grid lg:grid-cols-6 gap-8 mb-12">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <Link href="/homepage" className="flex items-center space-x-2 mb-6 hover-lift contemplative-transition">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary">
                <svg
                  className="w-6 h-6 text-white"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 2C12 2 15 5 15 9C15 11.21 13.21 13 11 13C8.79 13 7 11.21 7 9C7 5 10 2 10 2H12Z"
                    fill="currentColor"
                    opacity="0.8"
                  />
                  <path
                    d="M12 13C12 13 16 15 16 19C16 21.21 14.21 23 12 23C9.79 23 8 21.21 8 19C8 15 12 13 12 13Z"
                    fill="currentColor"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-crimson font-semibold">
                  Christ Faculty
                </h3>
                <p className="text-sm text-background/70 font-source-serif">
                  Life & Building ECT Hub
                </p>
              </div>
            </Link>
            
            <p className="text-background/80 font-source-serif leading-relaxed mb-6">
              Discover divine wisdom through multiple formats and connect with a community 
              of believers honoring the sacred journey of faith and spiritual growth.
            </p>
            
            <div className="flex space-x-4">
              {socialLinks?.map((social) => (
                <a
                  key={social?.name}
                  href={social?.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-background/10 rounded-lg flex items-center justify-center text-background/70 hover:text-background hover:bg-primary contemplative-transition"
                  aria-label={social?.name}
                >
                  <Icon name={social?.icon} size={20} />
                </a>
              ))}
            </div>
          </div>

          {/* Platform Links */}
          <div>
            <h4 className="font-inter font-semibold text-background mb-4">
              Platform
            </h4>
            <ul className="space-y-3">
              {footerLinks?.platform?.map((link) => (
                <li key={link?.name}>
                  <Link
                    href={link?.href}
                    className="text-background/70 hover:text-background font-source-serif contemplative-transition"
                  >
                    {link?.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Community Links */}
          <div>
            <h4 className="font-inter font-semibold text-background mb-4">
              Community
            </h4>
            <ul className="space-y-3">
              {footerLinks?.community?.map((link) => (
                <li key={link?.name}>
                  <Link
                    href={link?.href}
                    className="text-background/70 hover:text-background font-source-serif contemplative-transition"
                  >
                    {link?.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources Links */}
          <div>
            <h4 className="font-inter font-semibold text-background mb-4">
              Resources
            </h4>
            <ul className="space-y-3">
              {footerLinks?.resources?.map((link) => (
                <li key={link?.name}>
                  <Link
                    href={link?.href}
                    className="text-background/70 hover:text-background font-source-serif contemplative-transition"
                  >
                    {link?.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h4 className="font-inter font-semibold text-background mb-4">
              Support
            </h4>
            <ul className="space-y-3">
              {footerLinks?.support?.map((link) => (
                <li key={link?.name}>
                  <Link
                    href={link?.href}
                    className="text-background/70 hover:text-background font-source-serif contemplative-transition"
                  >
                    {link?.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Newsletter Signup */}
        <div className="border-t border-background/20 pt-8 mb-8">
          <div className="max-w-md">
            <h4 className="font-inter font-semibold text-background mb-2">
              Stay Connected
            </h4>
            <p className="text-background/70 font-source-serif text-sm mb-4">
              Get weekly spiritual insights and community updates.
            </p>
            <form className="flex gap-2">
              <input
                type="email"
                placeholder="Your email address"
                className="flex-1 px-4 py-2 rounded-lg bg-background/10 border border-background/20 text-background placeholder-background/50 focus:outline-none focus:ring-2 focus:ring-primary font-inter"
                required
              />
              <button 
                type="submit"
                className="px-6 py-2 bg-primary text-white rounded-lg font-inter font-medium hover:bg-primary/90 contemplative-transition"
              >
                Subscribe
              </button>
            </form>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-background/20 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-background/70 font-inter text-sm">
            © {currentYear} Pneuma Freedom Experience Ministry. All rights reserved.
          </p>
          
          <div className="flex items-center gap-6 text-sm">
            <Link
              href="/privacy"
              className="text-background/70 hover:text-background font-inter contemplative-transition"
            >
              Privacy Policy
            </Link>
            <Link
              href="/terms"
              className="text-background/70 hover:text-background font-inter contemplative-transition"
            >
              Terms of Service
            </Link>
            <Link
              href="/community-hub"
              className="text-background/70 hover:text-background font-inter contemplative-transition"
            >
              Accessibility
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}